import React, { useState, useCallback, useEffect } from 'react';
import '../styles/Gallery.css';

interface Photo {
  id: number;
  filename: string;
  original_name: string;
  sort_order: number;
  uploaded_at: string;
}

interface GalleryProps {
  photos: Photo[];
  apiBase: string;
}

const Gallery: React.FC<GalleryProps> = ({ photos, apiBase }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState<{ [key: number]: boolean }>({});

  const getImageUrl = (filename: string, thumbnail = false) => {
    const prefix = thumbnail ? 'thumb_' : '';
    return `${apiBase}/uploads/${prefix}${filename}`;
  };

  const navigatePrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  }, [photos.length]);

  const navigateNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  }, [photos.length]);

  const openFullscreen = (index: number) => {
    setCurrentIndex(index);
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isFullscreen) return;

    switch (e.key) {
      case 'ArrowLeft':
        navigatePrev();
        break;
      case 'ArrowRight':
        navigateNext();
        break;
      case 'Escape':
        closeFullscreen();
        break;
    }
  }, [isFullscreen, navigatePrev, navigateNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleImageLoad = (id: number) => {
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
  };

  if (photos.length === 0) {
    return (
      <section className="gallery">
        <div className="gallery-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <p>Photos coming soon</p>
        </div>
      </section>
    );
  }

  return (
    <section className="gallery">
      {/* Main featured image */}
      <div className="gallery-featured" onClick={() => openFullscreen(currentIndex)}>
        <div className="featured-image-container">
          {!imageLoaded[photos[currentIndex]?.id] && (
            <div className="image-placeholder">
              <div className="loading-spinner" />
            </div>
          )}
          <img
            src={getImageUrl(photos[currentIndex]?.filename)}
            alt={`Property view ${currentIndex + 1}`}
            className={imageLoaded[photos[currentIndex]?.id] ? 'loaded' : ''}
            onLoad={() => handleImageLoad(photos[currentIndex]?.id)}
          />
          <div className="featured-overlay">
            <span className="view-gallery-hint">Click to view full screen</span>
          </div>
        </div>

        {photos.length > 1 && (
          <>
            <button
              className="nav-button prev"
              onClick={navigatePrev}
              aria-label="Previous photo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              className="nav-button next"
              onClick={navigateNext}
              aria-label="Next photo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </>
        )}

        <div className="photo-counter">
          {currentIndex + 1} / {photos.length}
        </div>
      </div>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="gallery-thumbnails">
          <div className="thumbnails-container">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              >
                <img
                  src={getImageUrl(photo.filename, true)}
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen modal */}
      {isFullscreen && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(photos[currentIndex]?.filename)}
              alt={`Property view ${currentIndex + 1}`}
            />
          </div>

          <button
            className="fullscreen-close"
            onClick={closeFullscreen}
            aria-label="Close fullscreen"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {photos.length > 1 && (
            <>
              <button
                className="fullscreen-nav prev"
                onClick={navigatePrev}
                aria-label="Previous photo"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                className="fullscreen-nav next"
                onClick={navigateNext}
                aria-label="Next photo"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </>
          )}

          <div className="fullscreen-counter">
            {currentIndex + 1} / {photos.length}
          </div>

          <div className="fullscreen-hint">
            Press ESC or click outside to close • Use arrow keys to navigate
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
